import { Box, Divider, IconButton, Skeleton } from "@mui/material";
import React from "react";
import {
	ChevronLeftOutlined as CollapseIcon,
	ChevronRightOutlined as ExpandIcon
} from "@mui/icons-material";
import { zoullooColors } from "../../theme";
import {
	getUserProfileSectionStyles,
	getUserProfileContentStyles,
	getUserProfileInnerStyles
} from "./shared/UserProfileSectionStyles";

interface Props {
	isCollapsed: boolean;
	drawerWidth: number;
}

// Constants to avoid using array indices as keys
const MENU_ITEM_WIDTHS = [90, 85, 95, 88, 92, 100, 96];
const BOTTOM_MENU_ITEMS = ["messages", "notifications", "support", "settings"];

// Component for company header section
const CompanyHeaderSection: React.FC<{ isCollapsed: boolean }> = ({ isCollapsed }) => (
	<Box
		sx={{
			padding: isCollapsed ? "36px 0px" : "50px 0px",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			transition: "all 0.3s ease",
			position: "relative"
		}}
	>
		<Box
			sx={{
				position: "relative",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				width: isCollapsed ? "51px" : "210px",
				height: "auto",
				transition: "width 0.3s ease"
			}}
		>
			<Skeleton
				variant="rectangular"
				width={isCollapsed ? 51 : 210}
				height={isCollapsed ? 51 : 40}
				sx={{ borderRadius: "8px" }}
			/>
		</Box>

		{/* Toggle Button */}
		<IconButton
			size="small"
			sx={{
				color: "#838dab",
				padding: "0",
				"&:hover": {
					backgroundColor: "#f1f4fa",
					color: "#29345f"
				},
				transition: "all 0.3s ease",
				border: "1px solid #838dab",
				position: "fixed",
				top: "51px",
				right: isCollapsed ? "calc(100% - 63px)" : "calc(100% - 300px)",
				background: zoullooColors.background.default
			}}
		>
			{isCollapsed ? <ExpandIcon /> : <CollapseIcon />}
		</IconButton>
	</Box>
);

// Component for a single menu item
const MenuItemSkeleton: React.FC<{
	isCollapsed: boolean;
	iconWidth?: number;
	textWidth?: number;
	marginBottom?: string;
}> = ({ isCollapsed, iconWidth = 24, textWidth = 120, marginBottom = "5px" }) => (
	<Box
		sx={{
			height: "45px",
			display: "flex",
			alignItems: "center",
			gap: "8px",
			borderRadius: "8px",
			padding: "0",
			paddingLeft: isCollapsed ? "0px" : "24px",
			justifyContent: isCollapsed ? "center" : "flex-start",
			marginBottom
		}}
	>
		<Skeleton
			variant="rectangular"
			width={iconWidth}
			height={24}
			sx={{ borderRadius: "7px" }}
		/>
		{!isCollapsed && (
			<Skeleton
				variant="rectangular"
				width={textWidth}
				height={16}
				sx={{ borderRadius: "4px" }}
			/>
		)}
	</Box>
);

// Component for submenu items
const SubmenuSkeleton: React.FC<{ isCollapsed: boolean }> = ({ isCollapsed }) => {
	if (isCollapsed) return null;

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: "0px",
				position: "relative"
			}}
		>
			{/* Vertical line for submenu */}
			<Box
				sx={{
					position: "absolute",
					left: "32px",
					top: 0,
					bottom: 0,
					width: "1px",
					backgroundColor: "#E5E7EB",
					zIndex: 1
				}}
			/>

			{MENU_ITEM_WIDTHS.map((width, index) => (
				<Box
					key={`submenu-item-${width}-${index}`}
					sx={{
						height: "45px",
						display: "flex",
						alignItems: "center",
						paddingLeft: "39px",
						marginBottom: "0px"
					}}
				>
					<Skeleton
						variant="rectangular"
						width={`${width}%`}
						height={14}
						sx={{ borderRadius: "4px" }}
					/>
				</Box>
			))}
		</Box>
	);
};

// Component for bottom menu items
const BottomMenuSkeleton: React.FC<{ isCollapsed: boolean }> = ({ isCollapsed }) => (
	<Box
		sx={{
			pb: 1,
			display: "flex",
			justifyContent: "space-evenly",
			flexDirection: isCollapsed ? "column" : "row",
			transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
			minHeight: isCollapsed ? "200px" : "auto",
			gap: isCollapsed ? "8px" : "12px",
			alignItems: "center"
		}}
	>
		{BOTTOM_MENU_ITEMS.map(itemKey => (
			<Box
				key={itemKey}
				sx={{
					height: "40px",
					width: "40px",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
					transform: isCollapsed ? "translateY(0) scale(1)" : "translateX(0) scale(1)",
					opacity: 1
				}}
			>
				<Skeleton
					variant="rectangular"
					width={24}
					height={24}
					sx={{ borderRadius: "4px" }}
				/>
			</Box>
		))}
	</Box>
);

// Component for user profile section
const UserProfileSkeleton: React.FC<{ isCollapsed: boolean }> = ({ isCollapsed }) => (
	<Box sx={getUserProfileSectionStyles(isCollapsed)}>
		<Box sx={getUserProfileContentStyles(isCollapsed)}>
			<Box sx={getUserProfileInnerStyles()}>
				<Skeleton variant="circular" width={42} height={42} />
				{!isCollapsed && (
					<Box sx={{ minWidth: 0 }}>
						<Skeleton
							variant="rectangular"
							width={120}
							height={14}
							sx={{ borderRadius: "4px", mb: 0.5 }}
						/>
						<Skeleton
							variant="rectangular"
							width={80}
							height={12}
							sx={{ borderRadius: "4px" }}
						/>
					</Box>
				)}
			</Box>
			{!isCollapsed && (
				<Skeleton
					variant="rectangular"
					width={24}
					height={24}
					sx={{ borderRadius: "4px", marginRight: "4px" }}
				/>
			)}
		</Box>
	</Box>
);

const MenuSkeleton: React.FC<Props> = ({ isCollapsed, drawerWidth }) => {
	return (
		<Box
			sx={{
				overflow: "hidden",
				width: drawerWidth,
				height: "100vh",
				display: "flex",
				flexDirection: "column",
				backgroundColor: zoullooColors.background.default,
				borderRight: "1px solid #E5E7EB",
				border: "1px solid #838dab",
				borderRadius: "16px",
				transition: "width 0.3s ease"
			}}
		>
			{/* Company Header */}
			<CompanyHeaderSection isCollapsed={isCollapsed} />

			{/* Main Menu Items */}
			<Box
				sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", pl: "8px", pr: "40px" }}
			>
				<Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
					{/* Profile menu item with icon */}
					<MenuItemSkeleton isCollapsed={isCollapsed} />

					{/* Submenu items (only show when not collapsed) */}
					<SubmenuSkeleton isCollapsed={isCollapsed} />

					{/* My Application */}
					<MenuItemSkeleton isCollapsed={isCollapsed} textWidth={110} />

					{/* My Projects */}
					<MenuItemSkeleton isCollapsed={isCollapsed} textWidth={100} />
				</Box>
			</Box>

			<Divider variant="middle" sx={{ borderColor: "#838dab58", marginY: "10px" }} />

			{/* Bottom Menu Items */}
			<Box
				sx={{
					marginTop: "auto",
					transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)"
				}}
			>
				<BottomMenuSkeleton isCollapsed={isCollapsed} />

				{/* User Profile Section */}
				<UserProfileSkeleton isCollapsed={isCollapsed} />
			</Box>
		</Box>
	);
};

export default MenuSkeleton;
